import { SnapshotFrom, assign, setup } from 'xstate';
import { EamData } from '../types/orchestrator';
import { AddressFull } from '../../../shared/types/geolocation';
import { APP_ENV } from '../../../shared/config/constants';
import { m3toGJ } from '../../heat-calculator/helpers/calculations';

export type RegistrationDeskContext = {
  registrationType: 'moving' | 'renovation' | null;
  eamData: EamData | null;
  usageGJ: string | null;
  usageM3: string | null;
  salutation: 'sir' | 'madam' | null
  firstName: string | null;
  infix: string | null;
  lastName: string | null;
  dateOfBirth: Date | null;
  email: string | null;
  tel: string | null;
  billingAddress: AddressFull | null;
  startDate: Date | null;
  paymentMethod: 'directDebit' | 'ideal' | null;
  iban: string | null;
  isCustomUsageAccordionOpened: boolean
  seenSteps: Set<string>;
};

export type RegistrationDeskEventType = RegistrationDeskEvent['type'];

export type RegistrationDeskEventPayload<T extends RegistrationDeskEventType> = Omit<Extract<RegistrationDeskEvent, { type: T }>, 'type'>;

export type RegistrationDeskEventHandler<T extends RegistrationDeskEventType> = (values: RegistrationDeskEventPayload<T>) => void;

export type RegistrationDeskEventState = SnapshotFrom<typeof registrationDeskMachine>['value'];

type RegistrationDeskEvent =
  | {
    type: 'proceed.saveRegistrationType';
    registrationType: 'moving' | 'renovation';
    usageM3: string;
    eamData: EamData;
  }
  | {
    type: 'proceed.updateUsage';
    usageM3: string;
  }
  | {
    type: 'proceed.saveUsage';
    usageM3: string;
  }
  | {
    type: 'openCustomUsageAccordion'
  }
  | {
    type: 'closeCustomUsageAccordion'
  }
  | {
    type: 'proceed.savePersonalDetails';
    salutation: 'sir' | 'madam';
    firstName: string;
    infix: string | null;
    lastName: string;
    dateOfBirth: Date;
    email: string;
    tel: string;
  }
  | {
    type: 'proceed.saveDeliveryDetails';
    billingAddress: AddressFull | null;
    startDate: Date;
  }
  | {
    type: 'proceed.savePaymentDetails';
    paymentMethod: 'directDebit' | 'ideal';
    iban: string;
  }
  | {
    type: 'proceed.acceptTermsAndConditions';
  }
  | {
    type: 'info.viewPricingStructure';
  }
  | {
    type: 'info.viewEstimateExplanation';
  }
  | {
    type: 'goBack'
  }
  | {
    type: 'goBack.registrationType';
  }
  | {
    type: 'goBack.usage';
  }
  | {
    type: 'goBack.personalDetails';
  }
  | {
    type: 'goBack.deliveryDetails';
  }
  | {
    type: 'goBack.paymentDetails';
  }
  | {
    type: 'reset.changeDeliveryAddress';
  }
  | {
    type: 'reset.closeJourney';
  }
  | {
    type: 'reset.cancel';
  }
  | {
    type: 'reset';
  };

const initialContext: RegistrationDeskContext = {
  registrationType: null,
  eamData: null,
  usageGJ: null,
  usageM3: null,
  salutation: null,
  firstName: null,
  infix: null,
  lastName: null,
  dateOfBirth: null,
  email: null,
  tel: null,
  billingAddress: null,
  startDate: null,
  paymentMethod: null,
  iban: null,
  isCustomUsageAccordionOpened: false,
  seenSteps: new Set(),
};

if (APP_ENV === 'development' && !window.Cypress) {
  initialContext.salutation = 'madam';
  initialContext.firstName = 'Pietje';
  initialContext.lastName = 'Puk';
  initialContext.dateOfBirth = new Date('1996-08-21');
  initialContext.email = 'pietjepuk@uncinc.nl';
  initialContext.tel = '0612345678';
  initialContext.startDate = new Date();
  initialContext.paymentMethod = 'ideal';
  initialContext.iban = 'NL92VBYP6070080650';
}

export const registrationDeskMachine = setup({
  types: {
    context: {} as RegistrationDeskContext,
    events: {} as RegistrationDeskEvent,
  },
  actions: {
    assignFormData: assign(({ context, event }) => {
      const { type, ...payload } = event;

      if ('usageM3' in payload) {
        return {
          ...context,
          ...payload,
          usageGJ: String(Math.round(m3toGJ(Number(payload.usageM3)))),
        };
      }

      return {
        ...context,
        ...payload,
      };
    }),
    openCustomUsageAccordion: assign(({ context }) => {
      return {
        ...context,
        isCustomUsageAccordionOpened: true,
      };
    }),
    closeCustomUsageAccordion: assign(({ context }) => {
      return {
        ...context,
        isCustomUsageAccordionOpened: false,
      };
    }),
    markStepAsSeen: assign(({ context, self }) => {
      const currentSnapshot = self.getSnapshot();
      const machineId = currentSnapshot.machine.id;
      const meta = currentSnapshot.getMeta();

      // @ts-expect-error - For a currently unknown reason, the type of .value is not properly inferred
      const metaKey = `${machineId}.form.${currentSnapshot.value.form}`;

      if (!(metaKey in meta)) {
        return context;
      }

      if (typeof meta[metaKey] !== 'object' || typeof meta[metaKey] == null) {
        return context;
      }

      const stateMeta = meta[metaKey] as Record<string, any>;

      if (!('path' in stateMeta) || typeof stateMeta.path !== 'string') {
        return context;
      }

      return {
        ...context,
        seenSteps: context.seenSteps.add(stateMeta.path),
      };
    }),
    resetFormData: assign(() => {
      return initialContext;
    }),
  },
}).createMachine({
  context: initialContext,
  type: 'parallel',
  id: 'registrationDesk',
  states: {
    dialog: {
      initial: 'off',
      states: {
        off: {
          on: {
            'reset.closeJourney': {
              target: 'confirmJourneyClose',
            },
            'reset.changeDeliveryAddress': {
              target: 'confirmAddressReset',
            },
          },
        },
        confirmJourneyClose: {
          on: {
            reset: {
              target: 'off',
            },
            'reset.cancel': {
              target: 'off',
            },
          },
        },
        confirmAddressReset: {
          on: {
            reset: {
              target: 'off',
            },
            'reset.cancel': {
              target: 'off',
            },
          },
        },
      },
    },
    form: {
      initial: 'enteringRegistrationType',
      states: {
        enteringRegistrationType: {
          meta: {
            path: '/',
          },
          on: {
            'proceed.saveRegistrationType': {
              target: 'enteringUsage',
              actions: ['assignFormData', 'markStepAsSeen'],
            },
            reset: {
              target: 'enteringRegistrationType',
              actions: 'resetFormData',
            },
          },
        },
        enteringUsage: {
          meta: {
            path: '/verbruik-aanpassen',
          },
          on: {
            openCustomUsageAccordion: {
              actions: 'openCustomUsageAccordion',
            },
            closeCustomUsageAccordion: {
              actions: 'closeCustomUsageAccordion',
            },
            'proceed.updateUsage': {
              target: 'enteringUsage',
              actions: 'assignFormData',
            },
            'proceed.saveUsage': {
              target: 'enteringPersonalDetails',
              actions: ['assignFormData', 'markStepAsSeen'],
            },
            'info.viewPricingStructure': {
              target: 'viewingPricingStructure',
            },
            'info.viewEstimateExplanation': {
              target: 'viewingEstimateExplanation',
            },
            goBack: {
              target: 'enteringRegistrationType',
              actions: 'markStepAsSeen',
            },
            reset: {
              target: 'enteringRegistrationType',
              actions: 'resetFormData',
            },
          },
        },
        viewingPricingStructure: {
          meta: {
            path: '/prijsopbouw',
          },
          on: {
            goBack: {
              target: 'enteringUsage',
              actions: 'markStepAsSeen',
            },
          },
        },
        viewingEstimateExplanation: {
          meta: {
            path: '/toelichting-inschatting',
          },
          on: {
            goBack: {
              target: 'enteringUsage',
              actions: 'markStepAsSeen',
            },
          },
        },
        enteringPersonalDetails: {
          meta: {
            path: '/gegevens-invullen',
          },
          on: {
            'proceed.*': {
              target: 'enteringDeliveryDetails',
              actions: ['assignFormData', 'markStepAsSeen'],
            },
            goBack: {
              target: 'enteringUsage',
              actions: ['markStepAsSeen', 'assignFormData'],
            },
            reset: {
              target: 'enteringRegistrationType',
              actions: 'resetFormData',
            },
          },
        },
        enteringDeliveryDetails: {
          meta: {
            path: '/leveringsgegevens-invullen',
          },
          on: {
            'proceed.*': {
              target: 'enteringPaymentDetails',
              actions: ['assignFormData', 'markStepAsSeen'],
            },
            goBack: {
              target: 'enteringPersonalDetails',
              actions: 'assignFormData',
            },
            reset: {
              target: 'enteringRegistrationType',
              actions: 'resetFormData',
            },
          },
        },
        enteringPaymentDetails: {
          meta: {
            path: '/betaalgegevens-invullen',
          },
          on: {
            'proceed.*': {
              target: 'reviewingTermsAndConditions',
              actions: ['assignFormData', 'markStepAsSeen'],
            },
            goBack: {
              target: 'enteringDeliveryDetails',
              actions: ['markStepAsSeen', 'assignFormData'],
            },
            reset: {
              target: 'enteringRegistrationType',
              actions: 'resetFormData',
            },
          },
        },
        reviewingTermsAndConditions: {
          meta: {
            path: '/controle',
          },
          on: {
            'proceed.*': {
              target: 'completed',
              actions: 'markStepAsSeen',
            },
            'goBack.*': {
              actions: 'markStepAsSeen',
            },
            'goBack.usage': 'enteringUsage',
            'goBack.personalDetails': 'enteringPersonalDetails',
            'goBack.deliveryDetails': 'enteringDeliveryDetails',
            'goBack.paymentDetails': 'enteringPaymentDetails',
            goBack: {
              target: 'enteringPaymentDetails',
              actions: 'markStepAsSeen',
            },
            reset: {
              target: 'enteringRegistrationType',
              actions: 'resetFormData',
            },
          },
        },
        confirmReset: {
          on: {
            reset: {
              target: 'enteringRegistrationType',
              actions: 'resetFormData',
            },
          },
        },
        completed: {
          meta: {
            path: '/aanvraag-verzonden',
          },
          type: 'final',
        },
      },
    },
  },
});
